import * as React from "react";
import { IQuickJournalState } from "source/app/presentation/BottomBar/QuickJournal/Data/QuickJournalTypes";
import {
    GetQuickJournalCurrentTimeStamp,
} from "source/app/presentation/BottomBar/QuickJournal/Data/Clock/QuickJournalClock";
import { dispatchSetFunctionState } from "../../../../SharedTypes";
import {
    FocusTextAreaWithoutScrolling,
    GetCaretCoordinates,
    GetQuickJournalCursorViewportLeft,
    RefreshCaretCoordinatesMirror,
    RemoveCaretCoordinatesMirror,
} from "../Helpers/JournalCaptureTextAreaHelpers";
import { useQuickJournalTypingCharactersPerSecond } from "./useQuickJournalTypingCharactersPerSecond";
import { useJournalCaptureTextAreaInteractions } from "./useJournalCaptureTextAreaInteractions";

interface IUseJournalCaptureTextAreaProps {
    value: string;
    state: IQuickJournalState;
    setState: dispatchSetFunctionState<IQuickJournalState>;
    autoFocusOnMount?: boolean;
    onFocusChange?: (hasFocus: boolean) => void;
    onEnter?: (text: string) => void;
    onChange: (value: string) => void;
}

export interface IUseJournalCaptureTextAreaInteractionsProps {
    value: string;
    onChange: (value: string) => void;
    onFocusChange?: (hasFocus: boolean) => void;
    onFocusedStateChange: (hasFocus: boolean) => void;
    onEnter?: (text: string) => void;
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    autoAdjustHeight: () => void;
    recordTypingSample: (insertedCharacters: number) => void;
}

interface IUseJournalCaptureTextAreaResult {
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    cursorRef: React.RefObject<HTMLDivElement>;
    timeStamp: string;
    shouldHideTimeStamp: boolean;
    charactersPerSecond: number;
    isFocused: boolean;
    handleClick: () => void;
    handleChange: (nextValue: string) => void;
    handleBlur: () => void;
    handleFocus: () => void;
    handleBeforeInput: (event: React.FormEvent<HTMLTextAreaElement>) => void;
    handleInput: (event: React.FormEvent<HTMLTextAreaElement>) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function useJournalCaptureTextArea({
    value,
    state,
    setState,
    autoFocusOnMount,
    onFocusChange,
    onEnter,
    onChange,
}: IUseJournalCaptureTextAreaProps): IUseJournalCaptureTextAreaResult {
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const cursorRef = React.useRef<HTMLDivElement>(null);
    const typingRateRef = React.useRef<HTMLDivElement>(null);
    const cursorUpdateFrameIdRef = React.useRef<number | undefined>(undefined);
    const lastHorizontalOffsetRef = React.useRef<number | undefined>(undefined);
    const [timeStamp, setTimeStamp] = React.useState<string>(
        GetQuickJournalCurrentTimeStamp()
    );
    const [isFocused, setIsFocused] = React.useState(false);
    const {
        charactersPerSecond,
        recordTypingSample,
    } = useQuickJournalTypingCharactersPerSecond(value);

    const autoAdjustHeight = React.useCallback(() => {
        if (textAreaRef.current == null) {
            return;
        }

        const scrollHeight = textAreaRef.current.scrollHeight;
        const newHeight = value && value.length ? scrollHeight : 32;
        textAreaRef.current.style.height = `${newHeight}px`;
    }, [value]);

    const updateCursorPosition = React.useCallback(() => {
        if (!textAreaRef.current || !cursorRef.current) {
            return;
        }

        textAreaRef.current.scrollLeft = 0;
        const { selectionStart } = textAreaRef.current;
        const { left, top } = GetCaretCoordinates(
            textAreaRef.current,
            selectionStart
        );

        const containerLeft =
            cursorRef.current.parentElement?.getBoundingClientRect().left || 0;
        const cursorLeft =
            GetQuickJournalCursorViewportLeft() - containerLeft;
        const horizontalOffset = left - cursorLeft;

        cursorRef.current.style.left = `${cursorLeft}px`;
        cursorRef.current.style.top = `${top}px`;

        if (
            lastHorizontalOffsetRef.current === undefined ||
            Math.abs(lastHorizontalOffsetRef.current - horizontalOffset) > 0.1
        ) {
            lastHorizontalOffsetRef.current = horizontalOffset;
            setState((currentState: IQuickJournalState) =>
                Math.abs(currentState.horizontalOffset - horizontalOffset) <= 0.1
                    ? currentState
                    : {
                          ...currentState,
                          horizontalOffset,
                      }
            );
        }
        textAreaRef.current.style.transform = `translateX(${-horizontalOffset}px)`;
        textAreaRef.current.scrollLeft = 0;
    }, [setState]);

    const scheduleCursorPositionUpdate = React.useCallback(() => {
        if (cursorUpdateFrameIdRef.current !== undefined) {
            window.cancelAnimationFrame(cursorUpdateFrameIdRef.current);
        }

        cursorUpdateFrameIdRef.current = window.requestAnimationFrame(() => {
            updateCursorPosition();
            cursorUpdateFrameIdRef.current = undefined;
        });
    }, [updateCursorPosition]);

    React.useLayoutEffect(() => {
        if (!autoFocusOnMount) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            FocusTextAreaWithoutScrolling(textAreaRef.current);
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [autoFocusOnMount]);

    React.useEffect(() => {
        autoAdjustHeight();
        scheduleCursorPositionUpdate();
    }, [autoAdjustHeight, scheduleCursorPositionUpdate, value]);

    React.useEffect(() => {
        const handleResize = () => {
            autoAdjustHeight();
            RefreshCaretCoordinatesMirror(textAreaRef.current);
            scheduleCursorPositionUpdate();
        };

        window.addEventListener("resize", handleResize);
        window.visualViewport?.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.visualViewport?.removeEventListener("resize", handleResize);
        };
    }, [autoAdjustHeight, scheduleCursorPositionUpdate]);

    React.useEffect(() => {
        const textArea = textAreaRef.current;

        return () => {
            if (cursorUpdateFrameIdRef.current !== undefined) {
                window.cancelAnimationFrame(cursorUpdateFrameIdRef.current);
            }
            RemoveCaretCoordinatesMirror(textArea);
        };
    }, []);

    React.useEffect(() => {
        const updateTimeStamp = () =>
            setTimeStamp(GetQuickJournalCurrentTimeStamp());
        const intervalId = window.setInterval(updateTimeStamp, 1000);

        return () => window.clearInterval(intervalId);
    }, []);

    React.useEffect(() => {
        const syncFocusedState = () => {
            const hasTextareaFocus =
                textAreaRef.current !== null &&
                document.activeElement === textAreaRef.current;

            setIsFocused(hasTextareaFocus);
            onFocusChange?.(hasTextareaFocus);
        };

        document.addEventListener("focusin", syncFocusedState);
        document.addEventListener("focusout", syncFocusedState);
        document.addEventListener("pointerdown", syncFocusedState);
        window.addEventListener("blur", syncFocusedState);

        return () => {
            document.removeEventListener("focusin", syncFocusedState);
            document.removeEventListener("focusout", syncFocusedState);
            document.removeEventListener("pointerdown", syncFocusedState);
            window.removeEventListener("blur", syncFocusedState);
        };
    }, [onFocusChange]);

    const interactions = useJournalCaptureTextAreaInteractions({
        value,
        onChange,
        onFocusChange,
        onFocusedStateChange: setIsFocused,
        onEnter,
        textAreaRef,
        autoAdjustHeight,
        recordTypingSample,
    });

    const previousEntry = state.recentEntries[state.recentEntries.length - 1];
    const shouldHideTimeStamp = Boolean(
        previousEntry &&
            typeof previousEntry === "object" &&
            "timeStamp" in previousEntry &&
            previousEntry.timeStamp === timeStamp
    );

    return {
        textAreaRef,
        cursorRef,
        timeStamp,
        shouldHideTimeStamp,
        charactersPerSecond,
        isFocused,
        ...interactions,
    };
}
